/**
 * NIS2 Classification Algorithm - TypeScript Implementation
 * Based on Romanian DNSC Excel Tool
 *
 * @author DNSC Romania Implementation
 * @version 1.0.0
 */

import type { ImpactAnalysisData } from "../types/impact-analysis.ts";
import {
  type CompanyClassification,
  getClassification,
} from "./nis2-classifier-v2.ts";

// ============================================================================
// DATA STRUCTURES
// ============================================================================

export interface CompanyData {
  // Company Size (from EU SME definitions)
  companySize: "Mare" | "Mijlocie" | "Mică";

  // Geographic Conditions
  establishedInRomania: boolean;
  providesServicesInRomania: boolean;
  headquarteredInRomania?: boolean;
  isPublicAdminEntityRomania?: boolean;
  providesServicesInEuMemberState?: boolean;

  services: string[];

  // Risk Assessment
  isSoleProvider: boolean;
  publicImpactLevel: "RIDICAT" | "MEDIU" | "SCAZUT" | null;
  systemicRiskLevel: "RIDICAT" | "MEDIU" | "SCAZUT" | null;
  isNationalCritical: boolean;

  // Previous Classification
  isAlreadyCriticalEntity: boolean;

  // Sector Identification
  sectors: {
    // C2 - Critical Infrastructure
    isDNSProvider: boolean;
    isTLDRegistry: boolean;
    isQualifiedTrustProvider: boolean;
    isCentralPublicAdmin: boolean;

    // C3 - Main Sectors
    energySector: string[];
    transportSector: string[];
    bankingSector: string[];
    financialMarkets: string[];
    healthSector: string[];
    waterSector: string[];
    digitalInfraOther: string[];
    ictServiceManagement: string[];
    foodSector: string[];
    searchEngines: string[];

    // C4 - Digital Infrastructure Providers
    isPublicNetworkProvider: boolean;
    isPublicCommServiceProvider: boolean;
    isManagedSecurityProvider: boolean;

    // C7 - Public Administration & Other
    publicAdminServices: string[];
  };
  analizaImpact?: ImpactAnalysisData | null;

  article9Override?: {
    "9a": boolean | null;
    "9b": "RIDICAT" | "MEDIU" | "SCAZUT" | null;
    "9c": "RIDICAT" | "MEDIU" | "SCAZUT" | null;
    "9d": boolean | null;
  } | null;
}

export interface SectorGroups {
  C2: boolean; // Critical Infrastructure
  C3: boolean; // Main Sectors
  C4: boolean; // Digital Infrastructure
  C7: boolean; // Public Administration
}

export interface ClassificationConditions {
  // Essential Entity Paths
  K2: boolean; // C2 + Established in RO
  K3: boolean; // C3 + Large + Established in RO
  K4: boolean; // C4 + (Large OR Medium) + Services in RO
  M2: boolean; // K2 OR K3 OR K4 OR Already Critical
  M9: boolean; // C3 + High Risk Assessment
  M10: boolean; // C7 + National Critical

  // Important Entity Paths
  G4: boolean; // C4 + Small/Micro + Services in RO
  I3: boolean; // C3 + Medium + Established in RO
  K7: boolean; // C7 + (Medium OR Large) + Established in RO
  L2: boolean; // G4 OR I3 OR K7
  L9: boolean; // C3 + Medium Risk Assessment
  L10: boolean; // C7 + Any Risk (complex condition)
}

export interface ClassificationResult {
  classification: "ESSENTIAL" | "IMPORTANT" | "OUT_OF_SCOPE";
  classificationRO: string;
  sectorGroups: SectorGroups;
  conditions: ClassificationConditions;
  reasoning: string[];
  needsImpactAnalysis: boolean;
  classificationV2: string; // TODO clean
  fullClassificationV2: CompanyClassification;
}

// ============================================================================
// MAIN CLASSIFICATION FUNCTION
// ============================================================================

export function classifyNIS2Entity(data: CompanyData): ClassificationResult {
  // Step 1: Calculate Sector Groups
  const sectorGroups = calculateSectorGroups(data);

  // Step 2: Calculate all condition flags
  const conditions = calculateConditions(data, sectorGroups);

  // Step 3: Determine classification
  const classification = determineClassification(conditions);

  // Step 4: Determine if impact analysis is needed
  const needsImpactAnalysis = determineNeedsImpactAnalysis(
    data,
    sectorGroups,
    classification
  );

  const classsicationV2 = getClassification(data);
  // Return detailed result
  return {
    classificationV2: classsicationV2.classification,
    classification,
    classificationRO: getROClassification(classsicationV2.classification),
    fullClassificationV2: classsicationV2,
    sectorGroups,
    conditions,
    reasoning: classsicationV2.evaluations
      .filter((e) => e.result === classsicationV2.classification)
      .map((e) => e.reason),
    needsImpactAnalysis,
  };
}

// ============================================================================
// STEP 1: SECTOR GROUP CALCULATION
// ============================================================================

export function calculateSectorGroups(data: CompanyData): SectorGroups {
  const groups: SectorGroups = {
    C2: false, // Critical Infrastructure
    C3: false, // Main Sectors
    C4: false, // Digital Infrastructure
    C7: false, // Public Administration
  };

  // C2 - Critical Infrastructure (ANY = true)
  groups.C2 =
    data.sectors.isDNSProvider ||
    data.sectors.isTLDRegistry ||
    data.sectors.isQualifiedTrustProvider ||
    data.sectors.isCentralPublicAdmin;

  // C3 - Main Sectors (ANY service in these sectors)
  groups.C3 =
    data.sectors.energySector.length > 0 ||
    data.sectors.transportSector.length > 0 ||
    data.sectors.bankingSector.length > 0 ||
    data.sectors.financialMarkets.length > 0 ||
    data.sectors.healthSector.length > 0 ||
    data.sectors.waterSector.length > 0 ||
    data.sectors.digitalInfraOther.length > 0 ||
    data.sectors.ictServiceManagement.length > 0 ||
    data.sectors.foodSector.length > 0 ||
    data.sectors.searchEngines.length > 0;

  // C4 - Digital Infrastructure Providers
  groups.C4 =
    data.sectors.isPublicNetworkProvider ||
    data.sectors.isPublicCommServiceProvider ||
    data.sectors.isManagedSecurityProvider;

  // C7 - Public Administration & Other
  groups.C7 = data.sectors.publicAdminServices.length > 0;

  return groups;
}

// ============================================================================
// STEP 2: CONDITION CALCULATION
// ============================================================================

export function calculateConditions(
  data: CompanyData,
  sectorGroups: SectorGroups
): ClassificationConditions {
  const conditions: ClassificationConditions = {
    // Essential Entity Paths
    K2: false,
    K3: false,
    K4: false,
    M2: false,
    M9: false,
    M10: false,

    // Important Entity Paths
    G4: false,
    I3: false,
    K7: false,
    L2: false,
    L9: false,
    L10: false,
  };

  // Calculate K conditions (Essential paths)
  conditions.K2 = sectorGroups.C2 && data.establishedInRomania;

  conditions.K3 =
    sectorGroups.C3 && data.companySize === "Mare" && data.establishedInRomania;

  conditions.K4 =
    sectorGroups.C4 &&
    (data.companySize === "Mare" || data.companySize === "Mijlocie") &&
    data.providesServicesInRomania;

  // M2: Essential if any K condition OR already critical entity
  conditions.M2 =
    conditions.K2 ||
    conditions.K3 ||
    conditions.K4 ||
    data.isAlreadyCriticalEntity;

  // M9: C3 with high risk assessment
  conditions.M9 =
    sectorGroups.C3 &&
    !conditions.M2 && // Not already essential
    data.establishedInRomania &&
    (data.isSoleProvider ||
      data.isNationalCritical ||
      data.publicImpactLevel === "RIDICAT" ||
      data.systemicRiskLevel === "RIDICAT");

  // M10: C7 with national/regional critical status
  conditions.M10 =
    sectorGroups.C7 && data.isNationalCritical && data.establishedInRomania;

  // Calculate G, I, K7 conditions (Important paths)
  conditions.G4 =
    sectorGroups.C4 &&
    data.companySize === "Mică" &&
    data.providesServicesInRomania;

  conditions.I3 =
    sectorGroups.C3 &&
    data.companySize === "Mijlocie" &&
    data.establishedInRomania;

  conditions.K7 =
    sectorGroups.C7 &&
    (data.companySize === "Mijlocie" || data.companySize === "Mare") &&
    data.establishedInRomania;

  // L2: Important if any of G4, I3, K7
  conditions.L2 = conditions.G4 || conditions.I3 || conditions.K7;

  // L9: C3 with medium risk assessment
  conditions.L9 =
    sectorGroups.C3 &&
    !conditions.M9 && // Not high risk
    !conditions.M2 && // Not already essential
    data.establishedInRomania &&
    (data.publicImpactLevel === "MEDIU" || data.systemicRiskLevel === "MEDIU");

  // L10: C7 complex condition
  const sumK2toM9 = [
    conditions.K2,
    conditions.K3,
    conditions.K4,
    conditions.M2,
    conditions.M9,
  ].filter(Boolean).length;

  conditions.L10 =
    sectorGroups.C7 &&
    !conditions.M10 && // Not already essential via M10
    data.establishedInRomania &&
    (data.isSoleProvider ||
      data.publicImpactLevel === "MEDIU" ||
      data.publicImpactLevel === "RIDICAT" ||
      data.systemicRiskLevel === "MEDIU" ||
      data.systemicRiskLevel === "RIDICAT") &&
    sumK2toM9 === 0; // Not covered by other rules

  return conditions;
}

// ============================================================================
// STEP 3: FINAL CLASSIFICATION
// ============================================================================

export function determineClassification(
  conditions: ClassificationConditions
): "ESSENTIAL" | "IMPORTANT" | "OUT_OF_SCOPE" {
  // Check for Essential Entity
  if (conditions.M2 || conditions.M9 || conditions.M10) {
    return "ESSENTIAL";
  }

  // Check for Important Entity
  if (conditions.L2 || conditions.L9 || conditions.L10) {
    return "IMPORTANT";
  }

  // Otherwise, out of scope
  return "OUT_OF_SCOPE";
}

export function getROClassification(classification: string): string {
  const classifications: Record<string, string> = {
    ESSENTIAL: "Entitate critică",
    IMPORTANT: "Entitate importantă",
    OUT_OF_SCOPE: "Nu intră sub incidența legii",
    IMPACT: "Se Aplica ART. 9",
  };
  return classifications[classification] || "Necunoscut";
}

export function generateReasoning(
  classification: string,
  conditions: ClassificationConditions
): string[] {
  const reasons: string[] = [];

  if (classification === "ESSENTIAL") {
    if (conditions.K2) {
      reasons.push("Infrastructură critică stabilită în România");
    }
    if (conditions.K3) {
      reasons.push("Entitate mare în sectoare principale stabilită în România");
    }
    if (conditions.K4) {
      reasons.push(
        "Furnizor de infrastructură digitală mare/mijlociu cu servicii în România"
      );
    }
    if (conditions.M9) {
      reasons.push(
        "Entitate din sectoare principale cu evaluare de risc ridicat"
      );
    }
    if (conditions.M10) {
      reasons.push(
        "Administrație publică cu importanță critică națională/regională"
      );
    }
  } else if (classification === "IMPORTANT") {
    if (conditions.G4) {
      reasons.push(
        "Furnizor de infrastructură digitală mic cu servicii în România"
      );
    }
    if (conditions.I3) {
      reasons.push(
        "Entitate mijlocie în sectoare principale stabilită în România"
      );
    }
    if (conditions.K7) {
      reasons.push(
        "Entitate de administrație publică mijlocie/mare stabilită în România"
      );
    }
    if (conditions.L9) {
      reasons.push(
        "Entitate din sectoare principale cu evaluare de risc mediu"
      );
    }
    if (conditions.L10) {
      reasons.push("Entitate de administrație publică cu factori de risc");
    }
  } else {
    reasons.push("Entitatea nu îndeplinește criteriile directivei NIS2");
  }

  return reasons;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function determineNeedsImpactAnalysis(
  data: CompanyData,
  sectorGroups: SectorGroups,
  classification: string
): boolean {
  // Impact analysis needed for borderline cases
  return (
    classification === "OUT_OF_SCOPE" &&
    (sectorGroups.C3 || sectorGroups.C7) &&
    (data.companySize === "Mijlocie" || data.companySize === "Mare") &&
    (!data.publicImpactLevel || !data.systemicRiskLevel)
  );
}

export function validateInputData(data: CompanyData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check required fields
  if (!data.companySize) {
    errors.push("Dimensiunea companiei este obligatorie");
  }

  // Check risk levels if certain sectors are selected
  const needsRiskAssessment =
    calculateSectorGroups(data).C3 || calculateSectorGroups(data).C7;

  if (
    needsRiskAssessment &&
    !data.publicImpactLevel &&
    !data.systemicRiskLevel
  ) {
    errors.push("Evaluarea riscului este necesară pentru sectoarele alese");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// ============================================================================
// CAEN CODE MAPPINGS
// ============================================================================

export interface CAENMapping {
  sector: string;
  subsector: string;
  annex: "Anexa 1" | "Anexa 2";
  description: string;
}

// Simplified CAEN mappings - would be expanded with full Romanian CAEN codes
export const CAEN_MAPPINGS: Record<string, CAENMapping> = {
  // Energy sector
  "3511": {
    sector: "energySector",
    subsector: "electricity",
    annex: "Anexa 1",
    description: "Producerea energiei electrice",
  },
  "3512": {
    sector: "energySector",
    subsector: "electricity",
    annex: "Anexa 1",
    description: "Transportul energiei electrice",
  },
  "3513": {
    sector: "energySector",
    subsector: "electricity",
    annex: "Anexa 1",
    description: "Distribuția energiei electrice",
  },

  // Transport sector
  "5110": {
    sector: "transportSector",
    subsector: "air",
    annex: "Anexa 1",
    description: "Transport aerian de pasageri",
  },
  "4910": {
    sector: "transportSector",
    subsector: "rail",
    annex: "Anexa 1",
    description: "Transport feroviar de pasageri",
  },

  // Banking sector
  "6419": {
    sector: "bankingSector",
    subsector: "monetary",
    annex: "Anexa 1",
    description: "Alte intermedieri monetare",
  },

  // ICT services
  "6202": {
    sector: "ictServiceManagement",
    subsector: "consulting",
    annex: "Anexa 1",
    description: "Activități de consultanță în tehnologia informației",
  },
  "6201": {
    sector: "ictServiceManagement",
    subsector: "programming",
    annex: "Anexa 1",
    description: "Activități de realizare a soft-ului la comandă",
  },
};

export function mapCAENToSector(caenCode: string): CAENMapping | null {
  return CAEN_MAPPINGS[caenCode] || null;
}

// Absolute priority rules for certain entity types
export const ABSOLUTE_PRIORITY_RULES = {
  DNS_PROVIDERS: ["Furnizorii de servicii DNS"],
  TLD_REGISTRIES: ["Registrele de nume de domenii de nivel superior (TLD)"],
  CENTRAL_PUBLIC_ADMIN: ["Entitățile administrației publice centrale"],
  QUALIFIED_TRUST_PROVIDERS: ["Furnizorii calificați de servicii de încredere"],
};
